import { useEffect, useState } from 'react';
import {
    type Key,
    type TreeData as StatelyTreeData,
    type TreeOptions as StatelyTreeOptions,
    useTreeData as useStatelyTreeData,
} from 'react-stately';

import { useLiveRef } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

/**
 * Watch tree data and call the onChange callback when the tree data changes.
 */
export function useWatchTreeItems<T extends object>(
    tree: StatelyTreeData<T>,
    onChange: (items: StatelyTreeData<T>['items']) => void
) {
    const [initialItems] = useState(tree.items);
    const onChangeRef = useLiveRef(onChange);
    useEffect(() => {
        if (tree.items !== initialItems) {
            onChangeRef.current(tree.items);
        }
    }, [initialItems, tree.items, onChangeRef]);
}

/**
 * Get the parent of an item in the tree.
 */
function getParent<T extends object>(tree: TreeData<T>, key: Key) {
    const item = tree.getItem(key);
    assert(item, `Item with key ${key} not found in the tree.`);
    return item.parentKey ? (tree.getItem(item.parentKey) ?? null) : null;
}

/**
 * Get the index of an item in the tree.
 */
function getItemIndex<T extends object>(tree: TreeData<T>, key: Key) {
    const parent = getParent(tree, key);
    const children = parent ? (parent.children ?? []) : tree.items;
    return children.findIndex((item) => item.key === key);
}

type DragAndDropOptions = Parameters<typeof import('react-aria-components').useDragAndDrop>[0];

export interface TreeData<T extends object> extends StatelyTreeData<T> {
    /**
     * Get the parent of an item in the tree.
     */
    getParent(key: Key): TreeData<T>['items'][0] | null;
    /**
     * Get the index of an item in the tree.
     */
    getItemIndex(key: Key): number;

    /** The keys of the currently dragged items in the tree. */
    draggedKeys: Set<Key>;

    /** Sets the dragged keys. */
    setDraggedKeys(keys: Set<Key>): void;

    /** The keys of the currently expanded items in the tree. */
    expandedKeys: Set<Key>;

    /** Sets the expanded keys. */
    setExpandedKeys: (keys: Set<Key>) => void;

    /** Drag and drop options to connect to `useDragAndDrop`. */
    getDragAndDropOptions: () => {
        onDragStart: NonNullable<DragAndDropOptions['onDragStart']>;
        onDragEnd: NonNullable<DragAndDropOptions['onDragEnd']>;
        onDropActivate: NonNullable<DragAndDropOptions['onDropActivate']>;
    };
}

export interface TreeOptions<T extends object> extends StatelyTreeOptions<T> {
    /** The keys for the initially expanded items. */
    initialExpandedKeys?: Iterable<Key>;
}

/**
 * Manages state for an immutable tree data structure, and provides convenience methods to
 * update the data over time.
 */
export function useTreeData<T extends object>(options: TreeOptions<T>): TreeData<T> {
    const tree = useStatelyTreeData(options) as TreeData<T>;

    // Add helper functions.
    tree.getParent = (key: Key) => {
        return getParent(tree, key);
    };
    tree.getItemIndex = (key: Key) => {
        return getItemIndex(tree, key);
    };

    // Drag and drop.
    const [draggedKeys, setDraggedKeys] = useState(new Set<Key>());
    tree.draggedKeys = draggedKeys;
    tree.setDraggedKeys = setDraggedKeys;
    tree.getDragAndDropOptions = () => ({
        onDragStart: (event) => {
            setDraggedKeys(event.keys);
        },
        onDragEnd: () => {
            setDraggedKeys(new Set());
        },
        // Open the item when we drag over it.
        onDropActivate: (event) => {
            // If we drop on an item, select it.
            if (event.target.type === 'item' && event.target.dropPosition === 'on') {
                // If not already expanded.
                if (!expandedKeys.has(event.target.key)) {
                    setExpandedKeys(new Set(tree.expandedKeys).add(event.target.key));
                }
            }
        },
    });

    // Expanded keys.
    const [expandedKeys, setExpandedKeys] = useState(new Set<Key>(options?.initialExpandedKeys));
    tree.expandedKeys = expandedKeys;
    tree.setExpandedKeys = setExpandedKeys;

    // Fix remove function to remove the item from the expanded keys.
    const remove = tree.remove.bind(tree);
    tree.remove = (...keys) => {
        remove(...keys);

        const nextExpandedKeys = new Set(expandedKeys);
        for (const key of keys) {
            nextExpandedKeys.delete(key);
        }
        if (nextExpandedKeys.size !== expandedKeys.size) {
            setExpandedKeys(nextExpandedKeys);
        }
    };
    return tree;
}

/**
 * Generate a random key.
 * Useful if we don't have a stable key for an item.
 */
export function getRandomItemKey() {
    return Math.random().toString(36).substring(2, 9);
}

/**
 * Check if two keys are equal.
 */
export function equalKeys(a: Key | undefined | null, b: Key | undefined | null) {
    return (a ?? null) === (b ?? null);
}
